import { ExtraCallChange } from './extra_call_time';
import { resolveCompanyShiftBase } from './company_shift_context';
import axios from 'axios';
import { loginAdminWithCompatibility } from '../auth/adminRealmAuth';
import {
  requestAdminPasswordRecovery,
  resetAdminPassword
} from '../auth/adminRealmRecovery';
import { APIClient } from './api_helper';
import * as url from './url_helpter';

const api = new APIClient();
export { hasCompanyShiftContext } from './company_shift_context';

// Login Method
export const postJwtLogin = (data, headers) =>
  loginAdminWithCompatibility(data, headers);
export const forgotPassword = (data, headers) =>
  requestAdminPasswordRecovery(data, headers);
export const verifyOtp = (data, headers) =>
  api.create(url.VERIFY_OTP, data, headers);
export const resetPassword = (data, headers, authMode?) =>
  resetAdminPassword(data, headers, authMode);

export const updatePassword = (data, headers) =>
  api.create(url.UPDATE_PASSWORD, data, headers);

export const uploadFile = (data, headers) =>
  api.create(url.UPLOAD_FILE, data, headers);

export const uploadEventExpenseReceipt = (eventId: number, data: FormData, headers) =>
  api.create(`/non-billable-expense/event/${eventId}/receipts`, data, headers);

export const uploadJobReceipt = (jobId: number, data: FormData, headers) =>
  api.create(`/jobs/${jobId}/receipts`, data, headers);

// Contractor documents (#305 — admin review surface).
//
// `getContractorDocuments` returns the per-contractor list of W-9, I-9,
// insurance, license, and other uploads. `updateContractorDocumentStatus`
// transitions the row to APPROVED or REJECTED — rejection requires a
// non-empty `rejectionReason` enforced server-side. PATCH is used directly
// since the shared APIClient.update wrapper sends PUT.
export const getContractorDocuments = (contractorId: number | string) =>
  api.get(url.CONTRACTOR_DOCUMENTS(contractorId), {});

export const getContractorDocumentReviewQueue = (
  params: {
    type?: string;
    status?: string;
  } = {}
) => api.get(url.CONTRACTOR_DOCUMENT_REVIEW_QUEUE, params);

export const updateContractorDocumentStatus = (
  contractorId: number | string,
  docId: number | string,
  data: {
    status: 'APPROVED' | 'REJECTED';
    rejectionReason?: string;
    expiresAt?: string | null;
  }
) => axios.patch(url.CONTRACTOR_DOCUMENT_STATUS(contractorId, docId), data);

// User
// Retained tables paginate locally, so collect bounded server pages explicitly.
const readWorkforcePages = async (path: string) => {
  const first = await api.get(path, { page: 1, pageSize: 100 });
  const body = first.data;
  if (!Array.isArray(body?.data) || !Number.isSafeInteger(body?.meta?.totalPages)) {
    throw new Error('Workforce collection is unavailable');
  }
  const rows = [...body.data];
  for (let page = 2; page <= body.meta.totalPages; page += 1) {
    const next = await api.get(path, { page, pageSize: 100 });
    if (!Array.isArray(next.data?.data)) throw new Error('Workforce page is unavailable');
    rows.push(...next.data.data);
  }
  return { ...first, data: { ...body, data: rows } };
};
export const getAllUsers = () => readWorkforcePages(url.GET_USERS);
export const getNewApplicantsCount = () =>
  api.get(url.GET_NEW_APPLICANTS_COUNT, {});
export const getNewApplicants = (page, pageSize) =>
  api.get(url.GET_NEW_APPLICANTS, { page, pageSize });

export const searchNewApplicants = (searchTerm) =>
  api.get(url.SEARCH_NEW_APPLICANTS, { search: searchTerm });

export const getAllBlockedUsers = () => readWorkforcePages(url.GET_BLOCK_USER);
export const getUserById = (id) => api.get(`${url.GET_USER}/${id}`, {});
export const updateUser = (id, data) =>
  api.update(`${url.UPDATE_USER}/${id}`, data);
export const searchUsers = async (searchTerm, expectedCompanyId?: number) => {
  const base = await resolveCompanyShiftBase(expectedCompanyId);
  return base ? api.get(`${base}/extras/workers`, { search: searchTerm }) : api.get(`${url.SEARCH_USERS}/${searchTerm}`, {});
};
export const blockUser = (id, data) =>
  api.update(`${url.BLOCK_USER}/${id}`, data);

export const approveRejectUser = (id, data) =>
  api.create(`${url.APPROVE_REJECT_APPLICANT}/${id}`, data, {});

export const getUserJobHistory = (userId) =>
  api.get(`${url.GET_USER_JOB_HISTORY}/${userId}`, {});

// Contractor credential wallet (#307) — admin read-only.
export const getContractorCredentials = (contractorId: number) =>
  api.get(`${url.GET_CONTRACTOR_CREDENTIALS}/${contractorId}/credentials`, {});

export const getUsesFinancialHistory = (userId) =>
  api.get(`${url.GET_USER_FINANCIAL_HISTORY}/${userId}`, {});

export const getpayRoll = (
  year?: number,
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  search?: string,
  paymentStatus?: string
) => {
  const params: any = {};
  if (year) params.year = year;
  if (dateFilter) params.dateFilter = dateFilter;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (search) params.search = search;
  if (paymentStatus) params.paymentStatus = paymentStatus;
  return api.get(url.GET_PAYROLL, params);
};

// Quote
export const createQuote = (data, headers) =>
  api.create(url.CREATE_QUOTE, data, headers);
const getRetainedQuoteList = async (path, params = {}) => {
  const query = Object.fromEntries(Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== null && value !== ''
  ));
  const response = await api.get(path, { ...query, page: 1, pageSize: 100 });
  const rows = [...(response?.data?.data || [])];
  const totalPages = Number(response?.data?.meta?.totalPages || 1);
  for (let page = 2; page <= totalPages; page += 1) {
    const next = await api.get(path, { ...query, page, pageSize: 100 });
    rows.push(...(next?.data?.data || []));
  }
  return { ...response, data: { ...response.data, data: rows } };
};

export const getAllQuotes = (params = {}) => getRetainedQuoteList(url.GET_QUOTES, params);

export const getAllQuotesHistory = (id) =>
  api.get(`${url.GET_QUOTES_HISTORY}/${id}`, {});
export const getAllLastEventHistory = (id, data) =>
  api.create(`${url.GET_LAST_EVENT_HISTORY}/${id}`, data, {});
export const getLastEventHistoryForClient = (id, resource, token) =>
  api.get(`/quotes/client/history/${id}`, { resource, token });
export const getAllClientInvoiceHistory = (id) =>
  api.get(`${url.GET_CLIENT_INVOICE_HISTORY}/${id}`, {});
export const getCancelled = () => getRetainedQuoteList(url.GET_CANCELLED);
export const getQuoteById = (id) => api.get(`${url.GET_QUOTE_BY_ID}/${id}`, {});
// #872: unauthenticated client views — pass the possession token from the
// emailed link's ?token= (appended by api.get as a query param).
export const getQuoteByIdForClient = (eventId, token) =>
  api.get(`${url.GET_QUOTE_CLIENT}/${eventId}`, { token });
export const getDepositByIdForClient = (eventId, token) =>
  api.get(`/quotes/client/deposit/${eventId}`, { token });
export const getDepositHistoryForClient = (eventId, token) =>
  api.get(`/quotes/client/deposit-history/${eventId}`, { token });
export const acknowledgeDeposit = (eventId, data, token) =>
  api.update(
    `/quotes/deposit-actions/${eventId}?token=${encodeURIComponent(
      token || ''
    )}`,
    data
  );
export const checkUpdateQuote = (id) =>
  api.get(`${url.CHECK_UPDATE_QUOTE}/${id}`, {});
export const moveJobCheck = (data) =>
  api.create(`${url.MOVE_JOB_CHECK}`, data, {});

export const quoteMoveJob = (data) =>
  api.create(`${url.QUOTE_MOVE_JOB}`, data, {});

//test

export const changeQuoteStatus = (data, id) =>
  api.update(`${url.CHANGE_QUOTE_STATUS}/${id}`, data);
export const quoteUpdateRequest = (data, id) =>
  api.update(`${url.QUOTE_UPDATE_REQUEST}/${id}`, data);
export const changeQuoteStatusForClient = (data, id, token) =>
  api.update(
    `${url.CHANGE_QUOTE_STATUS}/${id}/client?token=${encodeURIComponent(
      token || ''
    )}`,
    data
  );
export const quoteUpdateRequestForClient = (data, id, token) =>
  api.update(
    `${url.QUOTE_UPDATE_REQUEST}/${id}/client?token=${encodeURIComponent(
      token || ''
    )}`,
    data
  );
export const updateQuote = (id, data) =>
  api.update(`${url.UPDATE_QUOTE}/${id}`, data);
export const getEventAgreement = (id) => api.get(`/quotes/agreement/${id}`, {});
export const updateEventAgreement = (id, data) =>
  api.update(`/quotes/agreement/${id}`, data);
export const updateEventPayments = (data, id) =>
  api.update(`/quotes/payments/${id}`, data);
export const updateEventNotes = (data, id) =>
  api.update(`${url.UPDATE_EVENT_NOTES}/${id}`, data);
export const deleteQuote = (id) => api.delete(`${url.DELETE_QUOTE}/${id}`, {});
export const cancelEvent = (id, data, headers) =>
  api.create(`${url.CANCEL_EVENT}/${id}`, data, headers);
export const sendDesposit = (id, data) =>
  api.create(`${url.SEND_DEPOSIT}/${id}`, data, {});
export const publishJobs = (id) => api.update(`${url.PUBLISH_JOBS}/${id}`, {});

export const changeUserPassword = (data, headers) =>
  api.create(url.CHANGE_PASSWORD, data, headers);

export const getAllEvents = (params) => api.get(url.GET_EVENTS, params);

export const getAllShifts = (id) => api.get(`${url.GET_SHIFTS}/${id}`, {});
export const checkAlreadyHired = (data, headers) =>
  api.create(url.ALREADY_HIRED, data, headers);

export const assignShiftRequest = (data, headers) =>
  api.create(url.ASSIGN_SHIFTS_REQUEST, data, headers);
export const assignShift = (data, headers) =>
  api.create(url.ASSIGN_SHIFTS, data, headers);

export const assignShiftBulk = (data, headers) =>
  api.create(url.ASSIGN_SHIFTS_BULK, data, headers);

export const assignShiftBulkRequest = (data, headers) =>
  api.create(url.ASSIGN_SHIFTS_REQUEST_BULK, data, headers);

export const unAssignShift = (data, headers) =>
  api.create(url.UNASSIGN_SHIFTS, data, headers);

export const CancelPendingBidRequest = (data, headers) =>
  api.create(url.CANCEL_BID_PENDING_REQUEST, data, headers);

export const getAllBidsByEventId = (id) =>
  api.get(`${url.GET_ALL_BIDS_BY_EVENTID}/${id}`, {});

export const deleteShift = (id) => api.delete(`${url.DELETE_SHIFT}/${id}`, {});
export const createLead = (data, id) =>
  api.update(`${url.CREATE_LEAD}/${id}`, data);
export const removeLead = (data, id) =>
  api.update(`${url.REMOVE_LEAD}/${id}`, data);
export const LastMinuteCancellation = (data, headers) =>
  api.create(url.LAST_MINUTE_CANCELLATION, data, headers);
export const RemoveLastMinuteCancellation = (data, headers) =>
  api.create(url.REMOVE_LAST_MINUTE_CANCELLATION, data, headers);
export const cancelShift = async (id, data, headers, expectedCompanyId?: number) => {
  const base = await resolveCompanyShiftBase(expectedCompanyId);
  if (!base) throw new Error('This action needs a Company Admin workspace. Switch to your company workspace from the company menu, then retry.');
  return api.create(`${base}/${id}/cancel`, data, headers);
};
export const getShiftCancellationRequests = (params = {}) =>
  api.get(url.SHIFT_CANCELLATION_REQUESTS, params);
export const getShiftLatenessNotices = (params = {}) =>
  api.get(url.SHIFT_LATENESS_NOTICES, params);
export const addShiftUser = (data, headers) =>
  api.create(url.ADD_USER_SHIFT, data, headers);

export const addExtra = async (data, headers) => {
  const base = await resolveCompanyShiftBase();
  return base ? api.create(`${base}/${data.shiftId}/extras`, {
    expectedShiftRevision: data.expectedShiftRevision, workerAccountId: data.userId,
    ...(data.callTimeMode ? { callTimeMode: data.callTimeMode } : {}),
    rate: data.rate, rateType: data.extraCallType, startTime: data.extraStartTime, endTime: data.extraEndTime,
    ...(data.extraStartDate ? { startDate: data.extraStartDate, endDate: data.extraEndDate } : {}),
    action: data.actionType === 'request' ? 'request' : 'assign',
    ...(data.allowEndedShift === true && data.actionType !== 'request' ? { allowEndedShift: true } : {}),
    ...(data.rateTypeOverride === true ? { rateTypeOverride: true } : {}),
    ...(data.bidId ? { bidId: data.bidId } : {}), ...(data.unlinkId ? { unlinkId: data.unlinkId } : {}),
    ...(data.adminNotes ? { adminNotes: data.adminNotes } : {})
  }, headers) : api.create(url.ADD_EXTRA, data, headers);
};

export const getShiftNumber = () => api.get(url.GET_SHIFT_NUMBER, {});

export const getAllBids = (id) => api.get(`${url.GET_BIDS}/${id}`, {});
export const deleteBid = (id, shiftId: number, expectedCancellationState?: string) => api.delete(`${url.DELETE_BID}/${id}?shiftId=${shiftId}&expectedCancellationState=${encodeURIComponent(expectedCancellationState ?? '')}`, {});
export const markBidsAsViewed = (bidIds: number[]) =>
  api.create(url.MARK_BIDS_VIEWED_BATCH, { bidIds }, {});

export const getAllJobs = (id) => api.get(`${url.GET_JOBS}/${id}`, {});
export const getFinancialStatement = (id) =>
  api.get(`${url.FINANCIAL_STATEMENT}/${id}`, {});
export const getAllContractorInvoice = (id) =>
  api.get(`${url.GET_CONTRACTOR_INVOICE}/${id}`, {});
export const invoiceUpdateRequest = (data, id, token) =>
  api.update(`${url.INVOICE_UPDATE_REQUEST}/${id}?token=${token}`, data);
export const updateJob = (data, id) =>
  api.update(`${url.UPDATE_JOB}/${id}`, data);
export const updatePayrollJob = (serialNumber, data) =>
  api.update(`${url.UPDATE_PAYROLL_JOB}/${serialNumber}`, data);
export const updateJobNotes = (data, id) =>
  api.update(`${url.UPDATE_JOB_NOTES}/${id}`, data);
export const deleteJob = (id) => api.delete(`${url.DELETE_JOB}/${id}`, {});

export const getExtraCallTime = async (shiftId: number, companyId: number) => {
  const base = await resolveCompanyShiftBase(companyId);
  if (!base) throw new Error('Extras defaults require a company workspace');
  return api.get(`${base}/${shiftId}/extras/call-time`, {});
};

export const changeExtraCallTime = async (shiftId: number, companyId: number,
  expectedShiftRevision: number, change: ExtraCallChange) => {
  const base = await resolveCompanyShiftBase(companyId);
  if (!base) throw new Error('Extras defaults require a company workspace');
  return api.update(`${base}/${shiftId}/extras/call-time`, { expectedShiftRevision, ...change });
};

export const updateExtraCallTime = async (data, headers) => {
  const base = await resolveCompanyShiftBase();
  return base ? api.update(`${base}/${data.shiftId}/extras/call-time`, {
    expectedShiftRevision: data.expectedShiftRevision, startTime: data.startTime, endTime: data.endTime,
    ...(data.startDate ? { startDate: data.startDate, endDate: data.endDate } : {})
  }) : api.create(url.UPDATE_EXTRA_CALL_TIME, data, headers);
};

export const getEventJobsUser = (params) =>
  api.get(url.GET_EVENT_JOBS_USER, params);
export const getEventJobsUserConfirmation = (params) =>
  api.get(url.GET_EVENT_JOBS_USER_CONFIRMATION, params);
export const getShiftReminders = (params) =>
  api.get(url.GET_SHIFT_REMINDERS, params);

export const createInvoice = (data, headers) =>
  api.create(url.CREATE_INVOICE, data, headers);
export const getWorkerInvoiceReview = (eventId: number, userId: number | null, manualWorkerId?: number) =>
  api.get('/attendances/invoices', { eventId, ...(manualWorkerId ? { manualWorkerId } : { userId }) });
export const getInvoiceHistory = (id) =>
  api.get(`${url.INVOICE_HISTORY}/${id}`, {});
export const approveInvoice = (data, headers) =>
  api.create(url.APPROVE_INVOICE, data, headers);
export const cancelSentInvoice = (data) =>
  api.create(url.CANCEL_SENT_INVOICE, data, {});
export const cancelApprovedInvoice = (serialNumber, jobIds?: number[]) =>
  api.create(
    `${url.CANCEL_APPROVED_INVOICE}/${encodeURIComponent(serialNumber)}`,
    jobIds ? { jobIds } : {},
    {}
  );

// Read-only listing of contractor-attached receipts for an invoice (#207).
// Backend route: GET /attendances/jobs/:jobId/receipts (auth-gated, owner-or-admin).
export const getInvoiceReceiptsForJob = (jobId: number | string) =>
  api.get(`${url.GET_INVOICE_RECEIPTS_FOR_JOB}/${jobId}/receipts`, {});
export const getAttendanceLogs = (params: {
  shiftId: number | string;
  jobId?: number | string;
  userId?: number | string;
  limit?: number;
}) => api.get(url.ATTENDANCE_LOG, params);

export const getClientInvoiceJobs = (id) =>
  api.get(`${url.CLINET_INVOICE_JOBS}/${id}`, {});
export const getClientInvoiceJobsForClient = (eventId, token) =>
  api.get(`${url.CLINET_INVOICE_JOBS}/${eventId}/client`, { token });
export const regenerateClientInvoiceJobs = (id) =>
  api.get(`${url.REGENERATE_INVOICE_JOBS}/${id}`, {});
export const createClientInvoiceJobs = (id, data, headers) =>
  api.create(`${url.CREATE_INVOICE_JOBS}/${id}`, data, headers);
export const changeClientInvoiceStatus = (data, id, headers) =>
  api.create(`${url.CHANGE_INVOICE_JOBS}/${id}`, data, headers);
export const changeClientInvoiceStatusForClient = (data, id, token) =>
  api.create(
    `${url.CHANGE_INVOICE_JOBS}/${id}/client?token=${encodeURIComponent(
      token || ''
    )}`,
    data,
    {}
  );
export const mergeInvoiceJobs = (data, headers) =>
  api.create(url.MERGE_INVOICE_JOBS, data, headers);
export const deleteInvoiceShift = (id) =>
  api.delete(`${url.DELETE_INVOICE_SHIFT}/${id}`, {});

export const getShiftUsers = (id) =>
  api.get(`${url.GET_SHIFT_USERS}/${id}`, {});

export const getUnlinkShifts = () => api.get(url.GET_UNLINKED_SHIFTS, {});
export const getUnlinkShiftsOfShifts = async (data, expectedCompanyId?: number) => {
  const base = await resolveCompanyShiftBase(expectedCompanyId);
  return api.create(base ? `${base}/unlinked/read` : url.GET_UNLINKED_SHIFTS_OF_SHIFTS, data, {});
};
export const updateUnlinkShift = (id, data) =>
  api.update(`${url.UPDATE_UNLINKED_SHIFT}/${id}`, data);
export const assignUnlinkShift = async (id, data) => {
  const base = await resolveCompanyShiftBase();
  return base ? api.create(`${base}/${data.shiftId}/unlinked/${id}/assign`, {
    workerAccountId: data.workerAccountId, expectedShiftRevision: data.expectedShiftRevision, rate: data.rate,
    ...(data.expectedSourceUpdatedAt ? { expectedSourceUpdatedAt: data.expectedSourceUpdatedAt } : {}),
    ...(data.bidId ? { bidId: data.bidId } : {})
  }, {}) : api.create(`${url.ASSIGN_UNLINK_SHIFT}/${id}`, data, {});
};

export const removeFromQuote = (id) =>
  api.update(`${url.REMOVE_FROM_QUOTE}/${id}`, {});

export const getAllChats = (params) => api.get(url.GET_ALL_CHATS, params);
export const getChatsByUserId = (id) =>
  api.get(`${url.GET_CHATS_BY_USER}/${id}`, {});
export const getChatDetailByChatId = (id, data) =>
  api.create(`${url.CHAT_DETAIL_BY_CHATID}/${id}`, data, {});
export const archieveChat = (id, data) =>
  api.create(`${url.ARCHIEVE_CHAT}/${id}`, data, {});
export const pinMessage = (id, data) =>
  api.create(`${url.PIN_MESSAGE}/${id}`, data, {});
export const findChat = (id) => api.get(`${url.FIND_CHAT}/${id}`, {});
export const getMessages = (id, params) =>
  api.get(`${url.GET_MESSAGES}/${id}`, params);
export const sendMessage = (data, headers) =>
  api.create(url.CREATE_MESSAGE, data, headers);

export const getAllAttedance = (id) =>
  api.get(`${url.GET_ATTENDANCE}/${id}`, {});
export const updateInternalAttendanceNote = (jobId: number, note: string) =>
  api.create(`/attendances/internal-note/${jobId}`, { note }, {});
export const createAttedance = (data, headers?: any) =>
  api.create(url.CREATE_ATTENDANCE, data, headers);
export const deleteAttedance = (id, data) =>
  api.create(`${url.DELETE_ATTENDANCE}/${id}`, data, {});

export const sendNowNotification = (data, headers) =>
  api.create(url.SEND_NOW_NOTIFICATION, data, headers);

export const scheduleNotification = (data, headers) =>
  api.create(url.SCHEDULE_NOTIFICATION, data, headers);

export const getScheduleNotification = () =>
  api.get(url.SCHEDULE_NOTIFICATION, {});
export const cancelscheduleNotification = (id) =>
  api.create(`${url.CANCEL_SCHEDULE_NOTIFICATION}/${id}`, {}, {});
export const getRecipientsByScheduleId = (id) =>
  api.get(`${url.GET_RECIPIENTS_BY_SCHEDULE_ID}/${id}`, {});

export const sendAnnouncement = (data, headers) =>
  api.create(url.SEND_NOTIFICATION, data, headers);

export const sendAnnouncement2 = (data, headers) =>
  api.create(url.SEND_NOTIFICATION2, data, headers);
export const sendMail = (data, headers) =>
  api.create(url.SEND_MAIL, data, headers);
export const sendMailToAll = (data, headers) =>
  api.create(url.SEND_MAIL_TO_ALL, data, headers);

export const getCalender = ({ month, year }) =>
  api.get(url.GET_CALENDER, { month, year });

export const sendReminders = (data, headers) =>
  api.create(url.SEND_REMINDERS, data, headers);

export const sendReminderWithAttachment = (data, file: File) => {
  const form = new FormData();
  form.append('payload', JSON.stringify(data));
  form.append('file', file, file.name);
  return api.create(url.SEND_REMINDER_WITH_ATTACHMENT, form, {});
};

export const getReminderAttachment = async (notificationId: number | string): Promise<Blob> => {
  const response = await axios.get(url.REMINDER_ATTACHMENT(notificationId), { responseType: 'blob' });
  return response.data;
};

export const confirmReminders = (data, headers) =>
  api.create(url.CONFRIM_REMINDERS, data, headers);

export const getContactInfo = () => api.get(url.GET_CONTACT_INFO, {});
export const updateContactInfo = (data, headers) =>
  api.create(url.GET_CONTACT_INFO, data, headers);

export const createPosition = (data, headers) =>
  api.create(url.POSITION, data, headers);
export const getAllPositions = () => api.get(url.POSITION, {});
export const updatePosition = (data, id) =>
  api.update(`${url.POSITION}/${id}`, data);
export const deletePosition = (id) => api.delete(`${url.POSITION}/${id}`, {});

export const createCategoryGear = (data, headers) =>
  api.create(url.CATEGORY_GEAR, data, headers);
export const getAllCategoryGears = () => api.get(url.CATEGORY_GEAR, {});
export const updateCategoryGear = (data, id) =>
  api.update(`${url.CATEGORY_GEAR}/${id}`, data);
export const deleteCategoryGear = (id) =>
  api.delete(`${url.CATEGORY_GEAR}/${id}`, {});

// Role-based gear catalog (Feature 48, #322).
export const getAllGearItems = () => api.get(url.GEAR_ITEMS, {});
export const getGearItem = (id) => api.get(`${url.GEAR_ITEMS}/${id}`, {});
export const createGearItem = (data, headers) =>
  api.create(url.GEAR_ITEMS, data, headers);
export const updateGearItem = (data, id) =>
  api.update(`${url.GEAR_ITEMS}/${id}`, data);
export const deleteGearItem = (id) => api.delete(`${url.GEAR_ITEMS}/${id}`, {});

// Per-position gear requirements.
export const getPositionGearRequirements = (positionId) =>
  api.get(url.POSITION_GEAR_REQUIREMENTS(positionId), {});
export const addPositionGearRequirement = (positionId, data, headers) =>
  api.create(url.POSITION_GEAR_REQUIREMENTS(positionId), data, headers);
export const updatePositionGearRequirement = (
  positionId,
  requirementId,
  data
) =>
  api.patch(
    `${url.POSITION_GEAR_REQUIREMENTS(positionId)}/${requirementId}`,
    data
  );
export const deletePositionGearRequirement = (positionId, requirementId) =>
  api.delete(
    `${url.POSITION_GEAR_REQUIREMENTS(positionId)}/${requirementId}`,
    {}
  );

// NOTIFICATIONS
export const getAllNotifications = (params) =>
  api.get(url.NOTIFICATIONS, params);
export const updateNotifications = (data, id) =>
  api.update(`${url.UPDATE_NOTIFICATIONS}/${id}`, data);
export const markAllReadNotifications = (id) =>
  api.create(`${url.MARK_ALL_READ_NOTIFICATIONS}/${id}`, {}, {});

// ROLES
export const getAllRoles = () => api.get(url.ROLES, {});

export const getAllRoleUsers = () => api.get(url.ROLE_USERS, {});
export const createRole = (data, headers) =>
  api.create(url.CREATE_ROLE_USER, data, headers);
export const deleteRoleUser = (id) =>
  api.delete(`${url.DELETE_USER}/${id}`, {});

export const getActualsByEventId = (id) =>
  api.get(`${url.GET_ACTUALS_BY_EVENT_ID}/${id}`, {});
export const actualMoveJob = (data) =>
  api.create(`${url.ACTUAL_MOVE_JOB}`, data, {});

export const getAllFaqs = () => api.get(url.FAQS, {});
export const createFaq = (data, headers) =>
  api.create(url.CREATE_FAQS, data, headers);
export const updateFaq = (id, data) =>
  api.update(`${url.UPDATE_FAQS}/${id}`, data);
export const deleteFaq = (id) => api.delete(`${url.DELETE_FAQS}/${id}`, {});

// POSITION CATEGORIES
export const createPositionCategory = (data, headers) =>
  api.create(url.POSITION_CATEGORIES, data, headers);
export const getAllPositionsCategories = () =>
  api.get(url.POSITION_CATEGORIES, {});
export const updatePositionCategory = (data, id) =>
  api.update(`${url.POSITION_CATEGORIES}/${id}`, data);

export const updateCategoryOrder = (data) =>
  api.update(url.POSITION_CATEGORIES_ORDER, data);
export const deletePositionCategory = (id) =>
  api.delete(`${url.POSITION_CATEGORIES}/${id}`, {});

export const createActuals = (id, data, headers) =>
  api.create(`${url.CREATE_ACTUALS}/${id}`, data, headers);
export const generateActuals = (id, data, headers) =>
  api.create(`${url.GENERATE_ACTUALS}/${id}`, data, headers);
export const getActualsApprovalArchive = (eventId: number) => api.get(`/actuals/archive/${eventId}`, {});

export const getActuals = (id, documentId?: number) => api.get(`${url.GET_ACTUALS}/${id}`, documentId ? { documentId } : {});
export const getActualsForClient = (eventId, token) =>
  api.get(`${url.GET_ACTUALS_CLIENT}/${eventId}`, { token });
export const approveActuals = (id, data, headers) =>
  api.create(`${url.APPROVE_ACTUALS}/${id}`, data, headers);
export const approveActualsForClient = (id, data, token) =>
  api.create(
    `${url.APPROVE_ACTUALS}/${id}/client?token=${encodeURIComponent(
      String(token ?? '')
    )}`,
    data,
    {}
  );
export const requesToEditActuals = (id, data, headers) =>
  api.create(`${url.REQUEST_TO_EDIT_ACTUALS}/${id}`, data, headers);
export const requestToEditActualsForClient = (id, data, token) =>
  api.create(
    `${url.REQUEST_TO_EDIT_ACTUALS}/${id}/client?token=${encodeURIComponent(
      String(token ?? '')
    )}`,
    data,
    {}
  );

export const actualDragAndDropJob = (data) =>
  api.create(`${url.ACTUAL_DRAG_AND_DROP_JOB}`, data, {});

export const deleteActualShift = (id) =>
  api.delete(`${url.DELETE_ACTUALS_SHIFT}/${id}`, {});

export const getCompanyProfile = (id) =>
  api.get(`${url.GET_COMPANY}/${id}`, {});
export const getPublicCompanyProfile = (id) =>
  api.get(`${url.GET_COMPANY}/${id}/public-profile`, {});
export const updateCompanyProfile = (id, data) =>
  api.update(`${url.UPDATE_COMPANY}/${id}`, data);

// Per-company contractor-document requirements (#304).
export const listCompanyDocumentRequirements = (companyId: number | string) =>
  api.get(url.COMPANY_DOCUMENT_REQUIREMENTS(companyId), {});
export const upsertCompanyDocumentRequirement = (
  companyId: number | string,
  documentType: string,
  data: { required?: boolean; notes?: string | null }
) =>
  api.update(
    url.COMPANY_DOCUMENT_REQUIREMENT_FOR_TYPE(companyId, documentType),
    data
  );
export const removeCompanyDocumentRequirement = (
  companyId: number | string,
  documentType: string
) =>
  api.delete(
    url.COMPANY_DOCUMENT_REQUIREMENT_FOR_TYPE(companyId, documentType),
    {}
  );

// Per-company dress code options.
export const listCompanyDressCodes = (companyId: number | string) =>
  api.get(url.COMPANY_DRESS_CODES(companyId), {});
export const createCompanyDressCode = (
  companyId: number | string,
  data: { label: string; description?: string | null; order?: number }
) => api.create(url.COMPANY_DRESS_CODES(companyId), data, {});
export const updateCompanyDressCode = (
  companyId: number | string,
  dressCodeId: number | string,
  data: { label: string; description?: string | null; order?: number }
) => api.update(url.COMPANY_DRESS_CODE_BY_ID(companyId, dressCodeId), data);
export const removeCompanyDressCode = (
  companyId: number | string,
  dressCodeId: number | string
) => api.delete(url.COMPANY_DRESS_CODE_BY_ID(companyId, dressCodeId), {});

export const createExpense = (data, headers) =>
  api.create(`${url.CREATE_EXPENSE}`, data, headers);

export const getExpensesByEventId = (id) =>
  api.get(`${url.GET_EXPENSES_BY_EVENT_ID}/${id}`, {});
export const deleteExpense = (id) =>
  api.delete(`${url.DELETE_EXPENSE}/${id}`, {});

export const updateExpense = (data) =>
  api.update(`${url.CREATE_EXPENSE}`, data);

export const getEmailTemplates = (type) =>
  api.get(`${url.GET_EMAIL_TEMPLATES}/${type}`, {});
export const createEmailTemplate = (data, headers) =>
  api.create(url.EMAIL_TEMPLATES, data, headers);
export const updateEmailTemplate = (id, data) =>
  api.update(`${url.EMAIL_TEMPLATES}/${id}`, data);
export const deleteEmailTemplate = (id) =>
  api.delete(`${url.EMAIL_TEMPLATES}/${id}`, {});

export const getTermsAndServices = () =>
  api.get(`${url.GET_TERMS_AND_SERVICES}`, {});
export const createTermsAndServices = (data, headers) =>
  api.create(url.TERMS_AND_SERVICES, data, headers);
export const updateTermsAndServices = (id, data) =>
  api.update(`${url.TERMS_AND_SERVICES}/${id}`, data);
export const deleteTermsAndServices = (id) =>
  api.delete(`${url.TERMS_AND_SERVICES}/${id}`, {});

// Bilateral post-shift reviews (#324, Feature 40)
export const createContractorReview = (shiftId: number | string, data: any) =>
  api.create(url.SHIFT_CONTRACTOR_REVIEW(shiftId), data, {});
export const createVenueReview = (shiftId: number | string, data: any) =>
  api.create(url.SHIFT_VENUE_REVIEW(shiftId), data, {});
export const getContractorReviewSummary = (
  userId: number | string,
  params: { page?: number; pageSize?: number } = {}
) => api.get(url.USER_CONTRACTOR_REVIEWS(userId), params);

// Incident reports (issue #320 / Feature 50)
export const getIncidentsForEvent = (eventId: number | string) =>
  api.get(`${url.GET_INCIDENTS_FOR_EVENT}/${eventId}/incidents`, {});
export const updateIncidentStatus = (
  incidentId: number | string,
  status: 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED'
) => api.patch(`${url.UPDATE_INCIDENT}/${incidentId}`, { status });

// Venue intelligence (Feature 47, issue #321)
export const getAllVenues = (
  params: { page?: number; pageSize?: number; search?: string } = {}
) => api.get(url.VENUES, params);
export const getVenue = (id: number | string) =>
  api.get(`${url.VENUES}/${id}`, {});
export const createVenue = (data: any) => api.create(url.VENUES, data, {});
export const updateVenue = (id: number | string, data: any) =>
  api.update(`${url.VENUES}/${id}`, data);
export const deleteVenue = (id: number | string) =>
  api.delete(`${url.VENUES}/${id}`, {});

export const getInvoiceNonBillableExpenses = (data: {
  serialNumber: string;
  status: string;
}) => api.create(url.GET_INVOICE_NON_BILLABLE_EXPENSES, data, {});

/** Bind only the filename returned by the same Job-scoped upload. */
export const uploadAndBindJobReceipt = async (
  jobId: number,
  file: File,
  field: 'BillableMiscExpenseReciept' | 'nonBillableMiscExpenseReciepts',
  headers
) => {
  const form = new FormData();
  form.append('file', file);
  const uploaded = await uploadJobReceipt(jobId, form, headers);
  const filename = uploaded?.data?.data?.filename;
  if (typeof filename !== 'string' || !filename) throw new Error('Receipt upload returned no filename.');
  return updateJob({ jobId, [field]: filename }, jobId);
};

export const getQuoteRevisionDraft = (id) => api.get(`/quotes/${id}/revision-draft`, {});
export const saveQuoteRevisionDraft = (id, data) => api.update(`/quotes/${id}/revision-draft`, data);
export const getQuoteEditorDraft = (id) => api.get(`/quotes/${id}/editor-draft`, {});
export const saveQuoteEditorDraft = (id, data) => api.update(`/quotes/${id}/editor-draft`, data);
export const createQuoteEditorVersion = (id, data) => api.create(`/quotes/${id}/editor-draft/versions`, data, {});
export const getQuoteEditorVersion = (id, key) => api.get(`/quotes/${id}/editor-draft/versions/${encodeURIComponent(key)}`, {});


export const getShiftGhosts = async (shiftId: number, companyId: number) => {
  const base = await resolveCompanyShiftBase(companyId);
  if (!base) throw new Error('Open your Company Admin workspace to load stock workers.');
  return api.get(`${base}/${shiftId}/ghosts`, {});
};
export const executeShiftGhost = async (shiftId: number, command: { extra?: boolean; [key: string]: unknown }, companyId: number) => {
  if (command.extra) throw new Error('Stock workers fill regular shift slots only. Select a regular slot.');
  const base = await resolveCompanyShiftBase(companyId);
  if (!base) throw new Error('Open your Company Admin workspace to change stock workers.');
  return api.create(`${base}/${shiftId}/ghosts`, command, {});
};

export const getShiftManualWorkers = async (shiftId: number, companyId?: number) => {
  const base = await resolveCompanyShiftBase(companyId);
  if (!base) throw new Error('This action needs a Company Admin workspace. Switch to your company workspace from the company menu, then retry.');
  return api.get(`${base}/${shiftId}/manual-workers`, {});
};
export const executeShiftManualWorker = async (shiftId: number, command: any, companyId?: number) => {
  const base = await resolveCompanyShiftBase(companyId);
  if (!base) throw new Error('This action needs a Company Admin workspace. Switch to your company workspace from the company menu, then retry.');
  return api.create(`${base}/${shiftId}/manual-workers`, command, {});
};
export const manualWorkerDocuments = async (shiftId:number,jobId:number,companyId:number,input?:any) => {
  const base=await resolveCompanyShiftBase(companyId);
  if (!base) throw new Error('This action needs a Company Admin workspace. Switch to your company workspace from the company menu, then retry.');
  const path=`${base}/${shiftId}/manual-workers/jobs/${jobId}/documents`;
  return input ? api.create(path,input,{}) : api.get(path,{});
};
export const approveManualAttendance = (jobId:number) => api.create('/attendances/approveAttendance',{jobIds:[jobId]},{});

export const getActualsSourceCorrections = (documentId: number) =>
  api.get(`/actuals/source-corrections/${documentId}`, {});
export const applyActualsSourceCorrections = (documentId: number, command: any) =>
  api.create(`/actuals/source-corrections/${documentId}`, command, {});

export const getBookingStructure = async (eventId: number, companyId: number) => {
  const base = await resolveCompanyShiftBase(companyId);
  if (!base) throw new Error('Open the Company Admin workspace before editing Bookings.');
  return api.get(`${base}/booking-structure/${eventId}`, {});
};

export const reviewBookingStaffingRemoval = async (eventId: number, input: unknown, companyId: number) => {
  const base = await resolveCompanyShiftBase(companyId);
  if (!base) throw new Error('Open the Company Admin workspace before editing Bookings.');
  return api.create(`${base}/booking-structure/${eventId}/staffing-review`, input, {});
};
export const changeBookingMainQuantity = async (shiftId: number, input: any, companyId: number) => {
  const base = await resolveCompanyShiftBase(companyId);
  if (!base) throw new Error('Open the Company Admin workspace before editing Bookings.');
  return api.create(`${base}/${shiftId}/main-quantity`, input, {});
};

export const changeBookingMainCallTime = async (shiftId: number, input: any, companyId: number) => {
  const base = await resolveCompanyShiftBase(companyId);
  if (!base) throw new Error('Open the Company Admin workspace before editing Bookings.');
  return api.create(`${base}/${shiftId}/main-call-time`, input, {});
};
export const createBookingShift = async (eventId: number, input: any, companyId: number) => {
  const base = await resolveCompanyShiftBase(companyId);
  if (!base) throw new Error('Open the Company Admin workspace before editing Bookings.');
  return api.create(`${base}/booking-structure/${eventId}/new-shift`, input, {});
};
